import { useEffect, useState } from 'react';
import { coreAPI } from '../lib/api';

/**
 * CR-4141: whether the signed-in user's role may approve or reject suppliers.
 *
 * Mirrors the backend gate exactly — a user is blocked only when they HAVE a
 * role that lacks both `can_approve_orders` and `is_admin_role` (e.g. Buyer).
 * Admins and users with no specific role are unaffected.
 *
 * Defaults to `true` so legitimate approvers never see a flash of hidden
 * controls while the profile request is in flight, and stays `true` if the
 * request fails — the backend still returns 403, so the UI never grants an
 * action the API would refuse.
 *
 * @param enabled - Skip the lookup when false (e.g. supplier portal mode).
 * @returns True when the user may approve/reject suppliers.
 */
export function useCanApproveSuppliers(enabled: boolean = true): boolean {
  const [canApprove, setCanApprove] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    coreAPI.getMyProfile()
      .then(res => {
        if (cancelled) return;
        const role = res.data.role_details;
        setCanApprove(!role || role.can_approve_orders || role.is_admin_role);
      })
      .catch(() => { /* leave default; backend still enforces the 403 */ });
    return () => { cancelled = true; };
  }, [enabled]);

  return canApprove;
}
